// 계약 사실화 1단계 정정9(26-07-29) — 계약 줄의 금액이 **어느 칸에 있고 무슨 뜻인지**
// 판정하는 정본. 계약서(서버 PDF)와 계약 상세(화면)가 이 한 벌을 함께 부른다.
//
// 무엇이 틀려 있었나 — 계약 등록 마법사에는 **판매가 칸이 없어** 판매 줄의 금액이
// '월 단가' 칸(unitPriceMonthly)에 저장된다. 정정7 이 계약서에만 폴백을 넣었고
// (판매가가 비면 월 칸의 금액을 판매가로 인쇄) 화면에는 넣지 않아, 같은 줄을 두고
//   • 계약서   : "18,000,000 (sale)"          = 판매가
//   • 계약 상세: "월 단가 18,000,000 / 판매가 -" = 월 구독료
// 라고 **서로 반대되는 진술**을 했다. 고객에게 나가는 문서와 운영자 화면이 어긋나는
// 것이므로 판정할 곳은 한 곳이어야 한다.
//
// 왜 이 패키지인가 — 서버(apps/api)와 화면(apps/windows)은 서로의 코드를 못 부르고,
// 둘 다 이 패키지를 부른다. 파생·판정 규칙을 여기 모으는 것이 이 리포의 관례다
// (spec-match · contract-pricing · robot-status · calendar, 그리고 바로 앞 정정5 가
// 올린 contract-line-reason). 폴백을 화면에 한 벌 더 만들면 이 차수가 네 라운드를
// 들여 없앤 '같은 규칙 두 벌' 이 그대로 되돌아온다.
//
// 이 함수는 **판정만** 한다. 문구·형식은 매체가 다르므로 각자 만든다:
//   • 계약서 PDF — `1,800,000 (sale)` (표 머리글이 'Unit/Mo' 라 값이 스스로 월액이
//     아니라고 말해야 한다. apps/api contract-pdf.service.contractLineAmountText)
//   • 계약 상세 — 판매가 칸에 금액을 넣고 '월 단가 칸에 입력됨' 을 덧붙인다(운영자가
//     마법사에서 잘못 넣었다는 것을 알 수 있게. apps/windows ContractDetailPage)
// 두 문구가 달라도 **어느 칸의 금액이 이 줄의 대금인가**는 한 벌이다.
//
// ⚠ 뿌리 원인은 **마법사에 판매가 칸이 없는 것**이다. 이 함수는 그 결과를 사실대로
// 보여 주는 것이고 입력을 고치는 것이 아니다(다음 차수 후보 — 마법사에 판매가 칸).
import { DeploymentReason } from './enums.js';
/**
 * **금액 0 은 '없음' 이다.** 이 리포의 표기 규칙이고 선례가 여럿이다(화면 정본은
 * apps/windows lib/amount-or-none, 계약서는 가격 카드의 bigint falsy 판정).
 * 금액 컬럼이 NOT NULL 이라 '비움' 이 0 으로 저장되므로, 그 0 을 금액으로 읽으면
 * 화면·문서가 "0원 계약" 이라는 거짓을 말한다.
 *
 * 숫자가 아닌 값(서버가 형식을 바꾼 경우)은 **접지 않고 원문을 돌려준다** — 조용히
 * 금액을 없애 버리지 않는다(같은 폴백 관례를 lib/amount-or-none 이 쓴다).
 */
function amountOrNone(value) {
    if (value === null || value === undefined)
        return null;
    const s = typeof value === 'bigint' ? value.toString() : String(value).trim();
    if (s === '')
        return null;
    return Number(s) === 0 ? null : s;
}
/**
 * 계약 줄의 금액이 어느 칸에 있고 무슨 뜻인지 판정한다(정본).
 *
 *   ① 판매 줄이 아니면(렌탈·파일럿·시연·행사·사내) — '월 단가' 칸이 그 줄의 금액이다.
 *   ② 판매 줄이고 판매가 칸에 금액이 있으면 — 그것이 판매가다.
 *   ③ 판매 줄인데 판매가 칸이 비었으면 — **월 단가 칸의 금액을 판매가로 읽는다**
 *      (마법사 경로). 판매가 칸만 읽으면 마법사로 만든 판매 계약의 금액이 화면·
 *      문서에서 **사라진다**. 값이 있는 칸에서 읽고, 어디서 읽었는지 밝힌다.
 *   ④ 두 칸 다 없으면 금액이 없는 줄이다(amount=null).
 */
export function resolveContractLineAmount(line) {
    const monthly = amountOrNone(line.unitPriceMonthly);
    const isSale = line.reason === DeploymentReason.SALE;
    if (!isSale)
        return { amount: monthly, isSale: false, fromMonthlyColumn: false };
    const listed = amountOrNone(line.listPriceKrw);
    if (listed !== null)
        return { amount: listed, isSale: true, fromMonthlyColumn: false };
    return { amount: monthly, isSale: true, fromMonthlyColumn: monthly !== null };
}
//# sourceMappingURL=contract-line-amount.js.map